
import React from "react";
import { i18nManager } from "../../../i18n";
import { ActionType, FormStep, Schemes, Utils } from "../../../utils";
import { PaymentMethod } from "../../../constants/PaymentMethod";
import { BoolHelper, Button, Classname, FormControl, TextInput } from "@rivtn/noseur";
import { ITransaction, ITransactionCharge, PloutousService, SanitizedResponse } from "../../../services";
import { CardForm, CardFormRef } from "@ploutous/react-library";

export interface PloutousCardProps {
    businessId: string;
    transaction: ITransaction;
    paymentSuccessful: () => void;
    stepState: [FormStep, React.Dispatch<React.SetStateAction<FormStep>>];
    loadingState: [boolean, React.Dispatch<React.SetStateAction<boolean>>];
    setActions: (action: (type: ActionType) => void, genOtp: () => void) => void;
    serviceErrorsState: [string[], React.Dispatch<React.SetStateAction<string[]>>];
    fetchTransaction: (onDone?: (result?: ITransaction, error?: any) => void) => void;
}

function PloutousCard(props: PloutousCardProps) {

    const transaction = props.transaction;
    const [_, setLoading] = props.loadingState;
    const [otpId, setOtpId] = React.useState("");
    const labels = i18nManager.Labels.method.card;
    const cardFormRef = React.useRef<CardFormRef>(null);
    const [otpToken, setOtpToken] = React.useState("");
    const ploutousService = PloutousService.getInstance();
    const [chargeData, setChargeData] = React.useState<any>({});
    const [testOtpCodes, setTestOtpCodes] = React.useState<string[]>([]);
    const [errorMessages, setErrorMessages] = React.useState<string[]>([]);
    const [generatingOtp, setGeneratingOtp] = React.useState<boolean>(false);
    const [cardHolderName, __] = React.useState(`${transaction.customer.first_name} ${transaction.customer.last_name}`.trim());
    const [___, setServiceErrors] = props.serviceErrorsState;
    const [step, setStep] = props.stepState;

    React.useEffect(() => props.setActions(proceedToPay.bind(PloutousCard), otpNextStep), []);
    React.useEffect(() => {
        if (BoolHelper.equalsAny(step, [FormStep.ENTER_OTP_3DS, FormStep.ENTER_OTP])) {
            generateOtp.bind(PloutousCard)(chargeData.__chargeData);
        }
    }, [step]);

    return (<>
        {step === FormStep.ENTER_CARD_DETAIL && (<><span className="title">{labels.title}</span>
            <div className="card-info" style={{ marginTop: 30 }}>
                <span className="title">{labels.card_info}</span>
                <span className="desc">{labels.card_desc}</span>
                <CardForm noNameInput manageRef={cardFormRef} container={{ style: { width: 400, height: 120 } }}
                    cardHolderName={cardHolderName}
                    style={{
                        cvvInput: {
                            marginLeft: "10px",
                        },
                        expiryCvvContainer: {
                            margin: "10px 0px",
                        },
                        highlightColors: {
                            success: "#008668"
                        },
                    }}
                    onMessage={(key, type, message) => {
                        if (type !== "error") {
                            setErrorMessages([]);
                            return;
                        }
                        setErrorMessages([message as string]);
                    }} />
            </div>
            <div className="card-info">
                <span className="title">{labels.card_holder_name}</span>
                <span className="desc">{labels.card_holder_name_desc}</span>
                <FormControl fill scheme={Schemes.RIVTN_PLOUTOUS}>
                    <TextInput defaultValue={cardHolderName} name="name" placeholder={labels.card_name_placeholder}
                        onInputComplete={(v) => cardFormRef.current?.setCardHolderName(v)} onFirstInput={() => setErrorMessages([])} />
                </FormControl>
            </div></>)}
        {(step === FormStep.THREE_DS) && (<iframe className="card-3ds-iframe" src={ploutousService.buildUrl(chargeData.result.redirect_3ds)} />)}
        {(step === FormStep.ENTER_OTP_3DS || step === FormStep.ENTER_OTP) && (<>
            <span className="title">{labels.enter_otp}</span>
            <span className="desc" style={{ marginTop: 20, textAlign: "center" }}>{labels.enter_sent_otp}</span>
            {!!testOtpCodes?.length && (<span className="desc" style={{ marginTop: 20, textAlign: "center", color: "red" }}>{labels.use_test_otp_codes}{testOtpCodes?.join(", ")}</span>)}
            <FormControl fill scheme={Schemes.RIVTN_PLOUTOUS} style={{ marginTop: 20, marginBottom: 20 }}>
                <TextInput id="p-otp_" defaultValue={otpToken} scheme={Schemes.RIVTN_PLOUTOUS} name="otp" placeholder={labels.otp_placeholder}
                    onInputComplete={(v) => setOtpToken(v)} onFirstInput={() => setErrorMessages([])} />
            </FormControl>
            <span className={Classname.build("link", { "noseur-disabled": generatingOtp })} onClick={() => generateOtp()}>
                {generatingOtp && (<i className="fa fa-spinner fa-spin" />)}
                <span>{labels.resend_otp}</span>
            </span>
        </>)}
        {errorMessages.map((errorMessage) => <span key={errorMessage} className="err">{errorMessage}</span>)}
        <Button outlined={step === "ENTER_OTP"} raised fill scheme={Schemes.RIVTN_PLOUTOUS} text={
            step === FormStep.ENTER_CARD_DETAIL
                ? i18nManager.Labels.common.pay_now
                : ((step === FormStep.ENTER_OTP_3DS || step === FormStep.ENTER_OTP)
                    ? i18nManager.Labels.common.continue
                    : i18nManager.Labels.common.back)}
            className="pay" disabled={!(!(errorMessages.length && step !== FormStep.ENTER_OTP))} onClick={() => {
                if (step === FormStep.ENTER_CARD_DETAIL) {
                    proceedToPay();
                } else {
                    validateOtp();
                }
            }} />
    </>);

    async function proceedToPay() {
        if (!cardHolderName.trim()) {
            setErrorMessages([labels.card_holder_name_required]);
            return;
        }
        setErrorMessages([]);
        if (step !== FormStep.ENTER_CARD_DETAIL) {
            setStep(FormStep.ENTER_CARD_DETAIL);
            return;
        }
        let cardData: string | undefined;
        setLoading(true);
        try {
            cardData = (await cardFormRef.current?.tokenize(transaction.transaction_id))?.data.nonce;
        } catch (error: any) {
            setErrorMessages([`${error}`]);
            setLoading(false);
            return;
        }
        ploutousService.initiateCharge(transaction, props.businessId, PaymentMethod.CARD, {
            is_nonce: true,
            card_data: cardData,
        }).then(({ sanitized }: SanitizedResponse<ITransactionCharge>) => {
            setChargeData({ result: sanitized.result, __chargeData: sanitized.extra_data?.charge_data });
            if (sanitized.result.requires_otp && sanitized.result.requires_3ds) {
                setStep(FormStep.ENTER_OTP_3DS);
            } else if (sanitized.result.requires_3ds) {
                setStep(FormStep.THREE_DS);
            } else if (sanitized.result.requires_otp) {
                setStep(FormStep.ENTER_OTP);
            } else if (!sanitized.result.requires_3ds && !sanitized.result.requires_otp) {
                setStep(FormStep.CONFIRM_PAYMENT);
            }
            // handle require token and require token
        }).catch((error: any) => {
            setServiceErrors(error.errors ?? [error.message]);
        }).finally(() => {
            setLoading(false);
        });
    }

    async function validateOtp() {
        setLoading(true);
        setErrorMessages([]);
        setChargeData({
            ...chargeData,
            result: {
                ...chargeData.result,
                requires_3ds: false,
            }
        });
        ploutousService.validateOtp(transaction.reference, props.businessId, chargeData.__chargeData, otpId, otpToken).then(({ sanitized }: SanitizedResponse<{ requires_3ds: boolean; }>) => {
            setOtpToken("");
            if (sanitized.requires_3ds) {
                setStep(FormStep.THREE_DS);
                return;
            }
            setStep(FormStep.CONFIRM_PAYMENT);
        }).catch((error: any) => {
            setErrorMessages(error.errors ?? [error.message]);
        }).finally(() => {
            setLoading(false);
        });
    }

    async function generateOtp(_chargeData?: string) {
        setErrorMessages([]);
        setGeneratingOtp(true);
        ploutousService.generateOtp(transaction.reference, props.businessId, (_chargeData ?? chargeData.__chargeData)).then(({ sanitized }: SanitizedResponse<{ i: string[]; otp_id: string; }>) => {
            setOtpId(sanitized.otp_id);
            if (sanitized.i) setTestOtpCodes(sanitized.i);
            Utils.toastSuccess(labels.opt_sent);
        }).catch((error: any) => {
            setErrorMessages(error.errors ?? [error.message]);
        }).finally(() => {
            setGeneratingOtp(false);
        });
    }

    async function otpNextStep() {
        alert("we got here");

    }

}

export default PloutousCard;
